/* eslint-disable react/prop-types */
'use client';
import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export const generate24HourOptions = (startTime = '8:00 AM') => {
  // Generate 30-minute intervals for full 24 hours (48 slots total)
  const allSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    allSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    allSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  const timeParts = startTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!timeParts) return allSlots;

  let [, hoursStr, minutesStr, period] = timeParts;
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;

  // Round minutes to nearest 30-minute interval
  const roundedMinutes = Math.ceil(minutes / 30) * 30;
  let effectiveHours = hours;
  let effectiveMinutes = roundedMinutes;

  if (effectiveMinutes >= 60) {
    effectiveHours = (effectiveHours + 1) % 24;
    effectiveMinutes = 0;
  }

  const effectiveStartTime = `${effectiveHours.toString().padStart(2, '0')}:${effectiveMinutes.toString().padStart(2, '0')}`;
  const startIndex = allSlots.indexOf(effectiveStartTime);
  if (startIndex === -1) return allSlots;

  return [...allSlots.slice(startIndex), ...allSlots.slice(0, startIndex)];
};

export const convert24To12 = (time24) => {
  if (!time24 || !time24.includes(':')) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export const convert12To24 = (time12) => {
  const [time, ampm] = time12.split(' ');
  let [hours, minutes] = time.split(':');
  let hour = parseInt(hours, 10);
  if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
  if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

export const generate12HourOptions = (startTime = '8:00 AM') => {
  return generate24HourOptions(startTime).map(convert24To12);
};

export const TimeSelector = ({
  value,
  onChange,
  placeholder,
  is24Hour,
  startTime = '8:00 AM',
  label = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const timeOptions = is24Hour
    ? generate24HourOptions(startTime)
    : generate12HourOptions(startTime);
  const displayValue = value ? (is24Hour ? value : convert24To12(value)) : '';

  const handleSelect = (selectedTime) => {
    const time24 = is24Hour ? selectedTime : convert12To24(selectedTime);
    onChange(time24);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filteredOptions = timeOptions.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full flex flex-col">
      {label && (
        <label className="mb-1 text-sm font-medium text-slate-700" htmlFor="time-selector">
          {label}
        </label>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild className="focus:ring-1 focus:ring-offset-0">
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="w-full justify-between focus-visible:ring-1 focus-visible:ring-offset-0  text-slate-800 font-normal hover:bg-white"
          >
            {displayValue || (
              <span className="text-slate-500">{placeholder || 'Select a time'}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          side="bottom"
          align="start"
        >
          <div className="border-b p-2">
            <input
              type="text"
              placeholder="Search time... (e.g., 9:30)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className="w-full text-left rounded-md px-3 py-1.5 text-sm font-medium hover:bg-green-50 hover:text-green-700 transition-colors focus:outline-none focus:bg-green-100"
                >
                  {option}
                </button>
              ))
            ) : (
              <p className="text-center text-sm text-slate-500 py-4">No matching times found.</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
